import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Tabs,
  Tab
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { 
  Description, 
  Psychology, 
  Summarize, 
  Visibility 
} from "@mui/icons-material";

function DocumentInfo({ selectedDoc }) {
  const [groupedEntities, setGroupEntities] = useState({});
  const [activeTab, setActiveTab] = useState(0); // Manage active tab state
  const [classificationType, setClassificationType] = useState(""); // Manage active tab state
  const [confidence, setConfidence] = useState(""); // Manage active tab state
  
  const iconStyle = { color: "#000814" }; 

  const stageIcons = {
    ocr: <Visibility fontSize="small" sx={iconStyle} />,
    classification: <Psychology fontSize="small" sx={iconStyle} />,
    summarization: <Summarize fontSize="small" sx={iconStyle} />,
    ner: <Description fontSize="small" sx={iconStyle} />,
  };

  useEffect(() => {
    const entities = selectedDoc.stages.ner?.output?.entities?.structured_entities ? selectedDoc.stages.ner.output.entities.structured_entities : [];
    const classification_type = selectedDoc.stages.classification?.output.classification || ""
    const confidence = selectedDoc.stages.classification?.output.confidence || ""
    console.log('classificationType: ' + classification_type)
    setClassificationType(classification_type)
    setConfidence(confidence)
    setGroupEntities(entities);
  }, [selectedDoc?.entities]);

  // Timeline stages from Firestore data
  const timelineStages = selectedDoc?.timeline || {};

  return (
    <div style={{ padding: "20px" }}>
      <Grid container spacing={2}>
        <Grid size={8} style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Tabs Navigation */}
          <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)} aria-label="document tabs">
            <Tab label="Details" />
            <Tab label="Text" />
            <Tab label="Timeline" />
          </Tabs> 
          {/* Tab Panels */}
          {activeTab === 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <div>
                          <Typography variant="h6" sx={{ textAlign: "left" }}>Summary</Typography>
                          <Typography sx={{ textAlign: "left", whiteSpace: "pre-wrap" }}>
                            {selectedDoc.stages.summarization?.output?.summary_text || "No summary available."}
                          </Typography>
                        </div>
                      </Grid>
                      <Grid size={8} style={{ height: '100vh' }}>
                        <iframe
                          src={selectedDoc?.file_url}
                          title="Document Preview"
                          width="100%"
                          height="100%"
                        ></iframe>
                      </Grid>
                      <Grid size={4}>
                        
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Extracted Text Tab */}
                {activeTab === 1 && (
                  <Box sx={{ mt: 2, p: 2, maxHeight: "600px", overflowY: "auto", }}>
                    <Typography variant="h6" sx={{ textAlign: "left" }}>Extracted Text</Typography>
                    <Paper
                      sx={{
                        backgroundColor: "white",
                        color: "black",
                        minHeight: "200px",
                        border: "1px solid #ddd",   // Optional: Light border for visibility
                        boxShadow: 1                // Optional: Adds slight elevation 
                      }}>
                      <Typography sx={{ whiteSpace: "pre-wrap", color: "black" }}>
                        {selectedDoc.stages.ocr.output?.text || "No extracted text available."}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Timeline Tab */}
                {activeTab === 2 && (
                  <Box sx={{ mt: 2, p: 2 }}>
                    <Typography variant="h6">Processing Timeline</Typography>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "left",
                        overflowX: "auto",
                        px: 4,
                        py: 14,
                        bgcolor: "#0d1b2a",
                        paddingTop: "10%"
                      }}
                    >
                      {Object.entries(selectedDoc.stages)
                        .filter(([stage]) => !["preprocessing", "postprocessing"].includes(stage))
                        .sort((a, b) => {
                          const timeA = new Date(a[1]?.timestamp || 0).getTime();
                          const timeB = new Date(b[1]?.timestamp || 0).getTime();
                          return timeA - timeB;
                        })
                        .map(([stage, data], index, arr) => {
                          const icon = stageIcons[stage.toLowerCase()] || <Description />;
                          const isEven = index % 2 === 0;

                          return (
                            <Box
                              key={stage}
                              sx={{
                                position: "relative",
                                width: 140,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              {/* Absolute text - always positioned */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: isEven ? "-100px" : "80px",
                                  textAlign: "center",
                                  color: "#00f5d4",
                                  width: "100%",
                                }}
                              >
                                <Typography variant="h6">{stage}</Typography>
                                <Typography variant="body2">Status: {data.status}</Typography>
                                <Typography variant="body2">
                                  {data.timestamp
                                    ? new Date(data.timestamp).toLocaleString()
                                    : "Pending"}
                                </Typography>
                              </Box>

                              {/* Icon row - aligned baseline */}
                              <Box
                                sx={{
                                  zIndex: 1,
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  backgroundColor: "#00f5d4",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {icon}
                              </Box>

                              {/* Connector line (horizontal to next item) */}
                              {index < arr.length - 1 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: "24px", // middle of icon (48px tall)
                                    left: "100%",
                                    width: "64px",
                                    height: "2px",
                                    backgroundColor: "#00f5d4",
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                )}

        </Grid>
        <Grid size={4}>
          <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
            {selectedDoc?.file_name}
          </Typography>
          <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
            {classificationType} : {confidence}
          </Typography>
          <Box sx={{ padding: 2 }}>
                {Object.keys(groupedEntities).length > 0 ? (
                  Object.keys(groupedEntities).map((group) => (
                    <Paper key={group} sx={{ padding: 2, marginBottom: 2, bgcolor: "#0d1b2a" }}>
                      <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
                        {group}
                      </Typography>
                      <Typography>
                        {Array.isArray(groupedEntities[group]) ? groupedEntities[group].join(", ") : ""}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography>No entities found.</Typography>
                )}
            </Box>
        </Grid>  
      </Grid>

      
    </div>
  );
}

export default DocumentInfo;
